/**
 * THE WORDS OF `/settings` — the six tabs, and the page around them.
 *
 * The tab table (`pages/settings-tabs.ts`) holds KEYS from here. Two surfaces
 * read them and they must not drift: the page draws the tab strip, and ⌘K
 * offers each tab BY NAME — which is the only reason a single page is better
 * than the five destinations it replaced.
 *
 * ── `terms` IS TRANSLATED, AND DELIBERATELY UNACCENTED ────────────────────
 *
 * These are the words someone TYPES, never words the screen draws. They stay
 * without accents on the Portuguese side because that is how half the world
 * types; the palette's sieve folds accents on both sides, so "fuso horário"
 * finds "fuso horario" and vice-versa. They are per-language because a synonym
 * list is the most language-bound thing in the app: "cnpj" means nothing in
 * English and "tax id" means nothing in Portuguese.
 */
export const settingsMessages = {
	en: {
		page: {
			title: "Settings",
			tabsLabel: "What to configure",
		},
		tabs: {
			cadastros: {
				label: "Registers",
				hint: "catalogues and tables the other data picks from",
				terms:
					"registers catalogue catalog price table services fees templates checklist quick replies support tables",
				empty: "No reference lists here yet.",
			},
			workspace: {
				label: "Workspace",
				hint: "name, timezone, brand",
				terms:
					"workspace name timezone time zone language locale currency country " +
					"brand logo company tax id legal name registration company address " +
					"agent signature business segment restrictions what not to say " +
					"public site slug public link how the agent appears",
			},
			conta: {
				label: "Your account",
				hint: "you and your password",
				terms:
					"my profile my name my password change password reset password e-mail email " +
					"photo avatar role job presence away available how many conversations at a time",
			},
			equipe: {
				label: "Team",
				hint: "who is here",
				terms:
					"people users members roles permissions admin owner member invite invitation " +
					"teams who sees what channel access funnel access deactivate reactivate",
			},
			acessos: {
				label: "Access",
				hint: "who sees and changes what",
				terms:
					"access permissions authorisation who sees what grants restrictions reach entity screen channel funnel agent role ben panel copilot",
			},
			segredos: {
				label: "Secrets",
				hint: "keys and secrets for integrations",
				terms:
					"secrets secret api key token password credentials integration erp http request function env vault",
			},
			webhooks: {
				label: "Webhooks",
				hint: "outside systems calling in",
				terms:
					"webhooks webhook stripe url endpoint secret signature integration callback events test",
			},
			canais: {
				label: "Channels",
				hint: "how they reach you",
				terms:
					"whatsapp instagram e-mail email site chat webchat connect channel " +
					"number qr code template who answers pause channel",
			},
			consumo: {
				label: "Consumption",
				hint: "what it cost",
				terms:
					"usage consumption cost spend how much it cost invoice bill charge " +
					"turns model tokens agent errors who answered",
			},
			organizacao: {
				label: "Workspaces",
				hint: "your workspaces",
				terms:
					"workspaces workspace switch organization network units branches stores franchise " +
					"headquarters head office multi workspace subsidiary shared credits " +
					"unit ceiling one invoice add unit",
			},
			sistema: {
				label: "System",
				hint: "theme and dependencies",
				terms:
					"theme appearance light dark dark mode " +
					"status health dependencies is it up version uptime models tools drivers",
			},
		},
	},
	"pt-BR": {
		page: {
			title: "Configurações",
			tabsLabel: "O que configurar",
		},
		tabs: {
			cadastros: {
				label: "Cadastros",
				hint: "catálogos e tabelas que os outros dados escolhem",
				terms:
					"cadastros catalogo tabela de precos servicos taxas modelos checklist respostas rapidas tabelas de apoio",
				empty: "Nenhuma lista de referência por aqui.",
			},
			workspace: {
				label: "Workspace",
				hint: "nome, fuso, marca",
				terms:
					"nome do workspace fuso horario timezone idioma locale moeda currency pais " +
					"marca logo logotipo empresa cnpj razao social inscricao endereco da empresa " +
					"assinatura do agente negocio segmento restricoes o que nao dizer " +
					"site publico slug link publico como o agente aparece",
			},
			conta: {
				label: "Sua conta",
				hint: "você e sua senha",
				terms:
					"meu perfil meu nome minha senha trocar senha alterar senha e-mail email " +
					"foto avatar cargo funcao presenca ausente disponivel quantas conversas de cada vez",
			},
			equipe: {
				label: "Equipe",
				hint: "quem está aqui",
				terms:
					"pessoas usuarios membros papeis permissoes admin owner member convite convidar " +
					"times equipes quem ve o que acesso a canais acesso a funis desativar reativar",
			},
			acessos: {
				label: "Acessos",
				hint: "quem vê e altera o quê",
				terms:
					"acesso acessos permissoes permissões autorizacao autorização quem ve o que concessoes restricoes alcance entidade tela canal funil agente papel ben painel copiloto",
			},
			segredos: {
				label: "Segredos",
				hint: "chaves e tokens de integração",
				terms:
					"segredos segredo chave de api api key token senha credenciais integracao erp http request funcao cofre",
			},
			webhooks: {
				label: "Webhooks",
				hint: "sistemas de fora chamando",
				terms:
					"webhooks webhook stripe url endpoint segredo assinatura integracao integração callback eventos teste",
			},
			canais: {
				label: "Canais",
				hint: "por onde chegam",
				terms:
					"whatsapp instagram e-mail email chat do site webchat conectar canal " +
					"numero qr code template quem responde pausar canal",
			},
			consumo: {
				label: "Consumo",
				hint: "quanto custou",
				terms:
					"uso consumo custo gasto quanto custou fatura conta cobranca " +
					"turnos modelo tokens erros dos agentes quem respondeu",
			},
			organizacao: {
				label: "Workspaces",
				hint: "seus workspaces",
				terms:
					"workspaces workspace trocar organizacao rede unidades filiais lojas franquia matriz " +
					"multi workspace bolsa compartilhada teto por unidade " +
					"uma fatura nova unidade",
			},
			sistema: {
				label: "Sistema",
				hint: "tema e dependências",
				terms:
					"tema aparencia claro escuro dark mode modo escuro " +
					"status saude dependencias esta no ar versao uptime modelos ferramentas drivers",
			},
		},
	},
	el: {
		page: {
			title: "Ρυθμίσεις",
			tabsLabel: "Τι να ρυθμίσεις",
		},
		tabs: {
			cadastros: {
				label: "Μητρώα",
				hint: "κατάλογοι και πίνακες που επιλέγουν τα άλλα δεδομένα",
				terms:
					"μητρώα κατάλογος τιμοκατάλογος υπηρεσίες χρεώσεις πρότυπα λίστα ελέγχου γρήγορες απαντήσεις",
				empty: "Δεν υπάρχουν ακόμη λίστες αναφοράς.",
			},
			workspace: {
				label: "Χώρος εργασίας",
				hint: "όνομα, ζώνη ώρας, μάρκα",
				terms:
					"ονομα χωρου εργασιας ζωνη ωρας timezone γλωσσα locale νομισμα currency χωρα " +
					"μαρκα λογοτυπο logo επιχειρηση αφμ tax id επωνυμια μητρωο διευθυνση επιχειρησης " +
					"υπογραφη πρακτορα κλαδος περιορισμοι τι να μην λεει " +
					"δημοσιος ιστοτοπος slug δημοσιος συνδεσμος πως εμφανιζεται ο πρακτορας",
			},
			conta: {
				label: "Ο λογαριασμός σου",
				hint: "εσύ και ο κωδικός σου",
				terms:
					"το προφιλ μου το ονομα μου ο κωδικος μου αλλαγη κωδικου επαναφορα κωδικου e-mail email " +
					"φωτογραφια avatar ρολος θεση παρουσια απων διαθεσιμος ποσες συνομιλιες καθε φορα",
			},
			equipe: {
				label: "Ομάδα",
				hint: "ποιος είναι εδώ",
				terms:
					"ατομα χρηστες μελη ρολοι δικαιωματα διαχειριστης ιδιοκτητης μελος admin owner member " +
					"προσκληση προσκλησεις ομαδες ποιος βλεπει τι προσβαση σε καναλια προσβαση σε χοανες " +
					"απενεργοποιηση επανενεργοποιηση",
			},
			acessos: {
				label: "Πρόσβαση",
				hint: "ποιος βλέπει και αλλάζει τι",
				terms:
					"προσβαση δικαιωματα εξουσιοδοτηση ποιος βλεπει τι περιορισμοι οντοτητα οθονη καναλι χοανη πρακτορας ρολος ben",
			},
			segredos: {
				label: "Μυστικά",
				hint: "κλειδιά και tokens ενσωματώσεων",
				terms: "μυστικά κλειδί api key token κωδικός διαπιστευτήρια ενσωμάτωση erp http",
			},
			webhooks: {
				label: "Webhooks",
				hint: "εξωτερικά συστήματα που καλούν",
				terms: "webhooks webhook stripe url endpoint μυστικό υπογραφή ενσωμάτωση συμβάντα δοκιμή",
			},
			canais: {
				label: "Κανάλια",
				hint: "από πού φτάνουν",
				terms:
					"whatsapp instagram e-mail email chat ιστοτοπου webchat συνδεση καναλι " +
					"αριθμος qr code προτυπο ποιος απανταει παυση καναλιου",
			},
			consumo: {
				label: "Κατανάλωση",
				hint: "πόσο κόστισε",
				terms:
					"χρηση καταναλωση κοστος δαπανη ποσο κοστισε τιμολογιο λογαριασμος χρεωση " +
					"γυροι μοντελο tokens σφαλματα πρακτορων ποιος απαντησε",
			},
			organizacao: {
				label: "Χώροι εργασίας",
				hint: "οι χώροι εργασίας σας",
				terms:
					"organismos diktyo monades ypokatastimata katastimata edra " +
					"οργανισμος δικτυο μοναδες υποκαταστηματα καταστηματα εδρα " +
					"κοινες μοναδες οριο μοναδας ενα τιμολογιο",
			},
			sistema: {
				label: "Σύστημα",
				hint: "θέμα και εξαρτήσεις",
				terms:
					"θεμα εμφανιση φωτεινο σκουρο dark mode σκοτεινη λειτουργια " +
					"κατασταση υγεια εξαρτησεις ειναι στον αερα εκδοση uptime μοντελα εργαλεια drivers",
			},
		},
	},
	es: {
		page: {
			title: "Ajustes",
			tabsLabel: "Qué configurar",
		},
		tabs: {
			cadastros: {
				label: "Registros",
				hint: "catálogos y tablas que eligen los demás datos",
				terms:
					"registros catalogo tabla de precios servicios tarifas plantillas checklist respuestas rapidas tablas de apoyo",
				empty: "Aún no hay listas de referencia.",
			},
			workspace: {
				label: "Espacio de trabajo",
				hint: "nombre, zona horaria, marca",
				terms:
					"nombre del espacio de trabajo zona horaria timezone idioma locale moneda currency pais " +
					"marca logo logotipo empresa cif nif razon social registro direccion de la empresa " +
					"firma del agente negocio sector restricciones que no decir " +
					"web publica slug enlace publico como aparece el agente",
			},
			conta: {
				label: "Tu cuenta",
				hint: "tú y tu contraseña",
				terms:
					"mi perfil mi nombre mi contrasena cambiar contrasena restablecer contrasena e-mail email " +
					"foto avatar rol puesto presencia ausente disponible cuantas conversaciones a la vez",
			},
			equipe: {
				label: "Equipo",
				hint: "quién está aquí",
				terms:
					"personas usuarios miembros roles permisos admin propietario miembro invitacion invitar " +
					"equipos quien ve que acceso a canales acceso a embudos desactivar reactivar",
			},
			acessos: {
				label: "Accesos",
				hint: "quién ve y cambia qué",
				terms:
					"acceso permisos autorizacion quien ve que concesiones restricciones alcance entidad pantalla canal embudo agente rol ben panel copiloto",
			},
			segredos: {
				label: "Secretos",
				hint: "claves y tokens de integración",
				terms:
					"secretos secreto clave de api api key token contrasena credenciales integracion erp http funcion",
			},
			webhooks: {
				label: "Webhooks",
				hint: "sistemas externos que llaman",
				terms:
					"webhooks webhook stripe url endpoint secreto firma integracion integración eventos prueba",
			},
			canais: {
				label: "Canales",
				hint: "por dónde llegan",
				terms:
					"whatsapp instagram e-mail email chat web webchat conectar canal " +
					"numero qr code plantilla quien responde pausar canal",
			},
			consumo: {
				label: "Consumo",
				hint: "cuánto costó",
				terms:
					"uso consumo coste gasto cuanto costo factura cuenta cobro " +
					"turnos modelo tokens errores de los agentes quien respondio",
			},
			organizacao: {
				label: "Workspaces",
				hint: "sus workspaces",
				terms:
					"organizacion red unidades sucursales tiendas franquicia matriz casa matriz " +
					"multi workspace bolsa compartida tope por unidad " +
					"una factura nueva unidad",
			},
			sistema: {
				label: "Sistema",
				hint: "tema y dependencias",
				terms:
					"tema apariencia claro oscuro dark mode modo oscuro " +
					"estado salud dependencias esta operativo version uptime modelos herramientas drivers",
			},
		},
	},
	fr: {
		page: {
			title: "Paramètres",
			tabsLabel: "Que configurer",
		},
		tabs: {
			cadastros: {
				label: "Référentiels",
				hint: "catalogues et tables où les autres données puisent",
				terms:
					"referentiels catalogue grille tarifaire services frais modeles checklist reponses rapides tables de support",
				empty: "Aucune liste de référence pour l’instant.",
			},
			workspace: {
				label: "Espace de travail",
				hint: "nom, fuseau, marque",
				terms:
					"nom de l'espace de travail fuseau horaire timezone langue locale devise currency pays " +
					"marque logo entreprise siret tva raison sociale immatriculation adresse de l'entreprise " +
					"signature de l'agent activite secteur restrictions ce qu'il ne faut pas dire " +
					"site public slug lien public comment l'agent apparait",
			},
			conta: {
				label: "Votre compte",
				hint: "vous et votre mot de passe",
				terms:
					"mon profil mon nom mon mot de passe changer de mot de passe reinitialiser mot de passe e-mail email " +
					"photo avatar role poste presence absent disponible combien de conversations a la fois",
			},
			equipe: {
				label: "Équipe",
				hint: "qui est là",
				terms:
					"personnes utilisateurs membres roles permissions admin proprietaire membre invitation inviter " +
					"equipes qui voit quoi acces aux canaux acces aux pipelines desactiver reactiver",
			},
			acessos: {
				label: "Accès",
				hint: "qui voit et modifie quoi",
				terms:
					"acces permissions autorisation qui voit quoi restrictions portee entite ecran canal pipeline agent role ben panneau copilote",
			},
			segredos: {
				label: "Secrets",
				hint: "clés et jetons d'intégration",
				terms:
					"secrets secret cle api api key token jeton mot de passe identifiants integration erp http fonction",
			},
			webhooks: {
				label: "Webhooks",
				hint: "systèmes externes qui appellent",
				terms:
					"webhooks webhook stripe url endpoint secret signature intégration integration événements test",
			},
			canais: {
				label: "Canaux",
				hint: "par où ils arrivent",
				terms:
					"whatsapp instagram e-mail email chat du site webchat connecter canal " +
					"numero qr code modele qui repond mettre le canal en pause",
			},
			consumo: {
				label: "Consommation",
				hint: "ce que ça a coûté",
				terms:
					"usage consommation cout depense combien ca a coute facture note frais " +
					"echanges modele tokens erreurs des agents qui a repondu",
			},
			organizacao: {
				label: "Espaces de travail",
				hint: "vos espaces de travail",
				terms:
					"organisation reseau unites filiales magasins franchise siege siege social " +
					"multi workspace credits partages plafond par unite " +
					"une facture nouvelle unite",
			},
			sistema: {
				label: "Système",
				hint: "thème et dépendances",
				terms:
					"theme apparence clair sombre dark mode mode sombre " +
					"statut sante dependances est-ce que ca tourne version uptime modeles outils drivers",
			},
		},
	},
	it: {
		page: {
			title: "Impostazioni",
			tabsLabel: "Cosa configurare",
		},
		tabs: {
			cadastros: {
				label: "Anagrafiche",
				hint: "cataloghi e tabelle da cui pescano gli altri dati",
				terms:
					"anagrafiche catalogo listino prezzi servizi tariffe modelli checklist risposte rapide tabelle di supporto",
				empty: "Ancora nessuna lista di riferimento.",
			},
			workspace: {
				label: "Spazio di lavoro",
				hint: "nome, fuso orario, marchio",
				terms:
					"nome dello spazio di lavoro fuso orario timezone lingua locale valuta currency paese " +
					"marchio logo azienda partita iva codice fiscale ragione sociale registro indirizzo dell'azienda " +
					"firma dell'agente attivita settore restrizioni cosa non dire " +
					"sito pubblico slug link pubblico come appare l'agente",
			},
			conta: {
				label: "Il tuo account",
				hint: "tu e la tua password",
				terms:
					"il mio profilo il mio nome la mia password cambia password reimposta password e-mail email " +
					"foto avatar ruolo mansione presenza assente disponibile quante conversazioni per volta",
			},
			equipe: {
				label: "Team",
				hint: "chi c'è qui",
				terms:
					"persone utenti membri ruoli permessi admin proprietario membro invito invita " +
					"team chi vede cosa accesso ai canali accesso alle pipeline disattiva riattiva",
			},
			acessos: {
				label: "Accessi",
				hint: "chi vede e modifica cosa",
				terms:
					"accesso permessi autorizzazione chi vede cosa restrizioni portata entita schermata canale pipeline agente ruolo ben pannello copilota",
			},
			segredos: {
				label: "Segreti",
				hint: "chiavi e token di integrazione",
				terms:
					"segreti segreto chiave api api key token password credenziali integrazione erp http funzione",
			},
			webhooks: {
				label: "Webhook",
				hint: "sistemi esterni che chiamano",
				terms: "webhook webhooks stripe url endpoint segreto firma integrazione eventi test",
			},
			canais: {
				label: "Canali",
				hint: "da dove arrivano",
				terms:
					"whatsapp instagram e-mail email chat del sito webchat collega canale " +
					"numero qr code modello chi risponde metti in pausa il canale",
			},
			consumo: {
				label: "Consumo",
				hint: "quanto è costato",
				terms:
					"uso consumo costo spesa quanto e costato fattura conto addebito " +
					"turni modello tokens errori degli agenti chi ha risposto",
			},
			organizacao: {
				label: "Workspace",
				hint: "i tuoi workspace",
				terms:
					"organizzazione rete unita filiali negozi franchising sede sede centrale " +
					"multi workspace crediti condivisi tetto per unita " +
					"una fattura nuova unita",
			},
			sistema: {
				label: "Sistema",
				hint: "tema e dipendenze",
				terms:
					"tema aspetto chiaro scuro dark mode modalita scura " +
					"stato salute dipendenze e attivo versione uptime modelli strumenti driver",
			},
		},
	},
	de: {
		page: {
			title: "Einstellungen",
			tabsLabel: "Was eingerichtet wird",
		},
		tabs: {
			cadastros: {
				label: "Stammdaten",
				hint: "Kataloge und Tabellen, aus denen die anderen Daten wählen",
				terms:
					"stammdaten katalog preisliste leistungen gebuehren vorlagen checkliste schnellantworten hilfstabellen",
				empty: "Noch keine Referenzlisten.",
			},
			workspace: {
				label: "Workspace",
				hint: "Name, Zeitzone, Marke",
				terms:
					"workspace name zeitzone timezone sprache locale wahrung currency land " +
					"marke logo firma steuernummer ust-id firmenname handelsregister firmenanschrift " +
					"signatur des agenten geschaft branche einschrankungen was nicht gesagt werden soll " +
					"offentliche website slug offentlicher link wie der agent auftritt",
			},
			conta: {
				label: "Dein Konto",
				hint: "du und dein Passwort",
				terms:
					"mein profil mein name mein passwort passwort andern passwort zurucksetzen e-mail email " +
					"foto avatar rolle position anwesenheit abwesend verfugbar wie viele konversationen gleichzeitig",
			},
			equipe: {
				label: "Team",
				hint: "wer hier ist",
				terms:
					"personen benutzer mitglieder rollen rechte admin inhaber mitglied einladung einladen " +
					"teams wer sieht was zugriff auf kanale zugriff auf pipelines deaktivieren reaktivieren",
			},
			acessos: {
				label: "Zugriffe",
				hint: "wer was sieht und ändert",
				terms:
					"zugriff berechtigungen autorisierung wer sieht was einschraenkungen reichweite entitaet bildschirm kanal trichter agent rolle ben panel copilot",
			},
			segredos: {
				label: "Geheimnisse",
				hint: "Schlüssel und Tokens für Integrationen",
				terms:
					"geheimnisse geheimnis api schluessel api key token passwort zugangsdaten integration erp http funktion",
			},
			webhooks: {
				label: "Webhooks",
				hint: "externe Systeme, die anrufen",
				terms:
					"webhooks webhook stripe url endpoint geheimnis signatur integration ereignisse test",
			},
			canais: {
				label: "Kanäle",
				hint: "wie sie dich erreichen",
				terms:
					"whatsapp instagram e-mail email website-chat webchat kanal verbinden " +
					"nummer qr code vorlage wer antwortet kanal pausieren",
			},
			consumo: {
				label: "Verbrauch",
				hint: "was es gekostet hat",
				terms:
					"nutzung verbrauch kosten ausgaben was hat es gekostet rechnung abrechnung gebuhr " +
					"durchgange modell tokens fehler der agenten wer hat geantwortet",
			},
			organizacao: {
				label: "Workspaces",
				hint: "Ihre Workspaces",
				terms:
					"organisation netzwerk einheiten filialen laden franchise zentrale " +
					"multi workspace geteilte credits obergrenze pro einheit " +
					"eine rechnung neue einheit",
			},
			sistema: {
				label: "System",
				hint: "Design und Abhängigkeiten",
				terms:
					"thema design aussehen hell dunkel dark mode dunkelmodus " +
					"status zustand abhangigkeiten lauft es version uptime modelle werkzeuge treiber",
			},
		},
	},
} as const;
